"use client";

import { useEffect, useMemo } from "react";
import {
  animate,
  useMotionValue,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { NODE_BLOOM_DURATION_MS } from "./timeline-constants";
import {
  type RgbColor,
  selectNeedleProximity,
  selectTimelineBloomRingInitialScale,
  selectTimelineCardTextColor,
  selectTimelineNodeCoreScale,
  selectTimelinePeriodCenterX,
  selectTimelinePeriodOpacity,
  selectTimelinePeriodWashGradient,
  selectTimelinePeriodWashHeight,
  selectTimelineSectionRgb,
  selectTimelineWashOpacity,
} from "./timeline-period-selectors";

export interface UseTimelinePeriodMotionModelInput {
  sectionColor: string;
  needleActive: boolean;
  needleX: MotionValue<number>;
  periodGlobalIndex: number;
  chartHeight: number;
  isSnapped: boolean;
}

export interface TimelinePeriodMotionModel {
  cardTextColor: MotionValue<string>;
  nodeCoreScale: MotionValue<number>;
  periodOpacity: MotionValue<number>;
  ringOpacity: MotionValue<number>;
  ringScale: MotionValue<number>;
  washGradient: string;
  washHeight: number;
  washOpacity: MotionValue<number>;
}

export function useTimelinePeriodMotionModel(
  input: UseTimelinePeriodMotionModelInput,
): TimelinePeriodMotionModel {
  const {
    chartHeight,
    isSnapped,
    needleActive,
    needleX,
    periodGlobalIndex,
    sectionColor,
  } = input;
  const sectionRgb = useMemo<RgbColor>(() => {
    return selectTimelineSectionRgb({ sectionColor });
  }, [sectionColor]);
  const periodCenterX = selectTimelinePeriodCenterX({ periodGlobalIndex });
  const washHeight = selectTimelinePeriodWashHeight({ chartHeight });
  const ringScale = useMotionValue(selectTimelineBloomRingInitialScale());
  const ringOpacity = useMotionValue(0);
  const proximity = useTransform(needleX, (needlePosition) =>
    selectNeedleProximity({
      needleActive,
      periodCenterX,
      needleX: needlePosition,
    }),
  );
  const periodOpacity = useTransform(proximity, (proximityValue) =>
    selectTimelinePeriodOpacity({
      needleActive,
      proximity: proximityValue,
    }),
  );
  const washOpacity = useTransform(proximity, (proximityValue) =>
    selectTimelineWashOpacity({
      needleActive,
      proximity: proximityValue,
    }),
  );
  const cardTextColor = useTransform(proximity, (proximityValue) =>
    selectTimelineCardTextColor({
      needleActive,
      proximity: proximityValue,
    }),
  );
  const nodeCoreScale = useTransform(proximity, (proximityValue) =>
    selectTimelineNodeCoreScale({
      needleActive,
      proximity: proximityValue,
    }),
  );
  const washGradient = useMemo(() => {
    return selectTimelinePeriodWashGradient({
      chartHeight,
      sectionRgb,
    });
  }, [chartHeight, sectionRgb]);

  useEffect(() => {
    if (!isSnapped) {
      return;
    }

    ringScale.set(selectTimelineBloomRingInitialScale());
    ringOpacity.set(0.3);

    const scaleAnimation = animate(ringScale, 1, {
      duration: NODE_BLOOM_DURATION_MS / 1000,
      ease: "easeOut",
    });

    const opacityAnimation = animate(ringOpacity, 0, {
      duration: NODE_BLOOM_DURATION_MS / 1000,
      ease: "easeOut",
    });

    return () => {
      scaleAnimation.stop();
      opacityAnimation.stop();
    };
  }, [isSnapped, ringOpacity, ringScale]);

  return {
    cardTextColor,
    nodeCoreScale,
    periodOpacity,
    ringOpacity,
    ringScale,
    washGradient,
    washHeight,
    washOpacity,
  };
}
